import { ListFilter, X } from "lucide-react";
import { useRef, useState } from "react";
import {
  AccordionContent,
  AccordionItem,
  AccordionPrimitive,
  AccordionTrigger,
} from "../../elements/accordion/accordion";
import { Badge } from "../../elements/badge/badge";
import { Button } from "../../elements/button/button";
import { Text } from "../../elements/text/text";
import { cn } from "../../lib/utils";
import type { AccordionFilterProps } from "../accordion-filter/accordion-filter";
import { AccordionFilter } from "../accordion-filter/accordion-filter";
import {
  PriceFilter,
  type PriceFilterProps,
  type PriceType,
} from "../price-filter/price-filter";

export type SelectedFilter = {
  groupTitle: string;
  value: string;
  label: string;
};

export type PriceFilterGroup = {
  kind: "price";
  title: string;
  resetLabel: string;
  defaultOpen?: boolean;
  className?: string;
  priceFilter: PriceFilterProps & {
    defaultSelectedPriceType?: PriceType;
  };
};

export type ProductFilterGroup = AccordionFilterProps | PriceFilterGroup;

export type ProductFiltersProps = {
  microcopies?: {
    showFiltersButton?: string;
    hideFiltersButton?: string;
    selectedFiltersTitle?: string;
    resetAllButton?: string;
    filtersTitle?: string;
    resetFiltersButton?: string;
    showResultsButton?: string;
    selectedFiltersMobileTitle?: string;
    clearFiltersButton?: string;
  };
  filterGroups: ProductFilterGroup[];
  selectedFilters?: SelectedFilter[];
  onFiltersChange?: (filters: SelectedFilter[]) => void;
  onResetAll?: () => void;
  priceFilter?: PriceFilterProps & {
    defaultSelectedPriceType?: PriceType;
  };
  defaultOpen?: boolean;
  className?: string;
  panelClassName?: string;
  onToggle?: (open: boolean) => void;
};

type State = {
  open: boolean;
  internalSelected: SelectedFilter[];
  openGroups: Record<string, boolean>;
};

const PRICE_FILTER_CHIP_VALUE = "__price_filter_range__";

function isPriceFilterChip(filter: SelectedFilter): boolean {
  return filter.value === PRICE_FILTER_CHIP_VALUE;
}

function getSelectedFilterKey(filter: SelectedFilter): string {
  return `${filter.groupTitle}::${filter.value}`;
}

function areSelectedFiltersEqual(
  left: SelectedFilter[],
  right: SelectedFilter[]
): boolean {
  if (left.length !== right.length) {
    return false;
  }

  const rightKeys = new Set(right.map(getSelectedFilterKey));

  return left.every((filter) => rightKeys.has(getSelectedFilterKey(filter)));
}

function getUncontrolledGroupProps(
  group: AccordionFilterProps
): Omit<AccordionFilterProps, "selectedValues"> {
  const { selectedValues: _selectedValues, ...rest } = group;
  return rest;
}

function isPriceFilterGroup(
  group: ProductFilterGroup
): group is PriceFilterGroup {
  return "kind" in group && group.kind === "price";
}

function isAccordionFilterGroup(
  group: ProductFilterGroup
): group is AccordionFilterProps {
  return !isPriceFilterGroup(group);
}

function getFilterLabel(
  options: AccordionFilterProps["options"],
  value: string
): string {
  const option = options.find((item) => item.value === value);
  return option?.label ?? value;
}

function getGroupSelectedFilters(
  group: AccordionFilterProps,
  values: string[]
): SelectedFilter[] {
  return values.map((value) => ({
    groupTitle: group.title,
    value,
    label: getFilterLabel(group.options, value),
  }));
}

function getDefaultSelectedFilters(
  filterGroups: ProductFilterGroup[]
): SelectedFilter[] {
  return filterGroups.flatMap((group) => {
    if (isPriceFilterGroup(group)) {
      return [];
    }

    return group.defaultSelectedValues.map((value) => ({
      groupTitle: group.title,
      value,
      label: getFilterLabel(group.options, value),
    }));
  });
}

function getGroupResetValues(group: AccordionFilterProps): string[] {
  if (group.resetSelectedValues !== undefined) {
    return group.resetSelectedValues;
  }

  if (group.variant === "radio") {
    return group.defaultSelectedValues;
  }

  return [];
}

function getResetSelectedFilters(
  filterGroups: ProductFilterGroup[]
): SelectedFilter[] {
  return filterGroups.flatMap((group) => {
    if (isPriceFilterGroup(group)) {
      return [];
    }

    return getGroupResetValues(group).map((value) => ({
      groupTitle: group.title,
      value,
      label: getFilterLabel(group.options, value),
    }));
  });
}

function getVisibleSelectedFilters(
  selectedFilters: SelectedFilter[],
  filterGroups: ProductFilterGroup[]
): SelectedFilter[] {
  const defaultRadioSelections = new Set(
    filterGroups.flatMap((group) => {
      if (!isAccordionFilterGroup(group) || group.variant !== "radio") {
        return [];
      }

      return group.defaultSelectedValues.map(
        (value) => `${group.title}::${value}`
      );
    })
  );

  return selectedFilters.filter(
    (filter) => !defaultRadioSelections.has(getSelectedFilterKey(filter))
  );
}

function getDefaultPriceRange(priceFilter: PriceFilterProps): [number, number] {
  return [priceFilter.min, priceFilter.max];
}

function isPriceRangeEqual(
  left: [number, number],
  right: [number, number]
): boolean {
  return left[0] === right[0] && left[1] === right[1];
}

function isPriceFilterAtDefault(
  priceFilter:
    | (PriceFilterProps & { defaultSelectedPriceType?: PriceType })
    | undefined
): boolean {
  if (!priceFilter) {
    return true;
  }

  const defaultPriceType = priceFilter.defaultSelectedPriceType ?? "dealer";
  const selectedPriceType = priceFilter.selectedPriceType ?? "dealer";
  return (
    isPriceRangeEqual(priceFilter.value, getDefaultPriceRange(priceFilter)) &&
    selectedPriceType === defaultPriceType
  );
}

function formatPriceValue(value: number): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function getPriceRangeChip(
  groupTitle: string,
  range: [number, number],
  currencySymbol: string
): SelectedFilter {
  return {
    groupTitle,
    value: PRICE_FILTER_CHIP_VALUE,
    label: `${currencySymbol} ${formatPriceValue(range[0])} - ${currencySymbol} ${formatPriceValue(range[1])}`,
  };
}

function syncPriceFilterChip(
  selectedFilters: SelectedFilter[],
  groupTitle: string,
  isAtDefault: boolean,
  range: [number, number],
  currencySymbol: string
): SelectedFilter[] {
  const withoutPriceChip = selectedFilters.filter(
    (filter) => !(filter.groupTitle === groupTitle && isPriceFilterChip(filter))
  );

  if (isAtDefault) {
    return withoutPriceChip;
  }

  return [
    ...withoutPriceChip,
    getPriceRangeChip(groupTitle, range, currencySymbol),
  ];
}

function getClosedGroups(
  filterGroups: ProductFilterGroup[]
): Record<string, boolean> {
  const openGroups: Record<string, boolean> = {};

  for (const group of filterGroups) {
    openGroups[group.title] = false;
  }

  return openGroups;
}

function renderPriceFilterGroup(
  group: PriceFilterGroup,
  priceFilter: PriceFilterProps & { defaultSelectedPriceType?: PriceType },
  isResetDisabled: boolean,
  isOpen: boolean,
  onOpenChange: (open: boolean) => void
) {
  return (
    <div className={cn("accordion-filter", group.className)} key={group.title}>
      <AccordionPrimitive
        collapsible
        onValueChange={(value) => onOpenChange(value === "open")}
        type="single"
        value={isOpen ? "open" : ""}
      >
        <AccordionItem value="open">
          <AccordionTrigger
            className="pt-4 pb-2"
            onReset={() => {
              priceFilter.onReset?.();
              onOpenChange(false);
            }}
            resetDisabled={isResetDisabled}
            resetId={`accordion-filter-${group.title.replace(/\s+/g, "-").toLowerCase()}`}
            resetLabel={group.resetLabel}
            triggerVariant="reset"
          >
            <div className="flex w-full items-center justify-between">
              <Text variant="body-sm-bold">{group.title}</Text>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="py-1">
              <PriceFilter {...priceFilter} className={priceFilter.className} />
            </div>
          </AccordionContent>
        </AccordionItem>
      </AccordionPrimitive>
    </div>
  );
}

export function ProductFilters(props: Readonly<ProductFiltersProps>) {
  const priceFilterGroup = props.priceFilter
    ? ({
        kind: "price",
        title: props.priceFilter.microcopies.title,
        resetLabel: props.priceFilter.microcopies.resetLabel,
        defaultOpen: props.defaultOpen,
        priceFilter: props.priceFilter,
      } as PriceFilterGroup)
    : undefined;

  const filterGroups = priceFilterGroup
    ? [...props.filterGroups, priceFilterGroup]
    : props.filterGroups;
  const selectedPriceFilterGroup = filterGroups.find(
    (group): group is PriceFilterGroup => isPriceFilterGroup(group)
  );

  const [state, setState] = useState<State>({
    open: !!props.defaultOpen,
    internalSelected:
      props.selectedFilters || getDefaultSelectedFilters(filterGroups),
    openGroups: {},
  });
  const initialPriceStateByGroup = useRef<
    Record<string, { range: [number, number]; selectedPriceType: PriceType }>
  >({});
  const selectedFilters = props.selectedFilters ?? state.internalSelected;
  const latestSelectedRef = useRef<SelectedFilter[]>(selectedFilters);

  function setOpen(open: boolean) {
    const isMobile = globalThis.matchMedia("(max-width: 639px)").matches;
    if (isMobile) {
      document.body.style.overflow = open ? "hidden" : "";
    }
    setState((prev) => ({ ...prev, open }));
    props.onToggle?.(open);
  }
  const visibleSelectedFilters = getVisibleSelectedFilters(
    selectedFilters,
    filterGroups
  );
  const resetSelectedFilters = getResetSelectedFilters(filterGroups);
  const areGroupFiltersAtResetState = areSelectedFiltersEqual(
    selectedFilters,
    resetSelectedFilters
  );
  const isPriceAtDefault = selectedPriceFilterGroup
    ? isPriceFilterAtDefault(selectedPriceFilterGroup.priceFilter)
    : true;
  const isAtDefaultState = areGroupFiltersAtResetState && isPriceAtDefault;

  latestSelectedRef.current = selectedFilters;

  function commitSelected(next: SelectedFilter[]) {
    if (props.selectedFilters === undefined) {
      setState((prev) => ({ ...prev, internalSelected: next }));
    }

    props.onFiltersChange?.(next);
  }

  function setGroupOpen(groupTitle: string, open: boolean) {
    setState((prev) => ({
      ...prev,
      openGroups: {
        ...prev.openGroups,
        [groupTitle]: open,
      },
    }));
  }

  function handleRemove(filter: SelectedFilter) {
    const isSelectedPriceChip =
      filter.groupTitle === selectedPriceFilterGroup?.title &&
      isPriceFilterChip(filter);

    if (isSelectedPriceChip && selectedPriceFilterGroup) {
      selectedPriceFilterGroup.priceFilter.onReset?.();

      const next = latestSelectedRef.current.filter(
        (selected) =>
          !(
            selected.groupTitle === selectedPriceFilterGroup.title &&
            isPriceFilterChip(selected)
          )
      );

      commitSelected(next);
      setGroupOpen(selectedPriceFilterGroup.title, false);
      return;
    }

    const group = props.filterGroups.find(
      (currentGroup): currentGroup is AccordionFilterProps =>
        currentGroup.title === filter.groupTitle &&
        isAccordionFilterGroup(currentGroup)
    );

    if (!group) {
      const next = latestSelectedRef.current.filter(
        (f) => !(f.groupTitle === filter.groupTitle && f.value === filter.value)
      );
      commitSelected(next);
      setGroupOpen(filter.groupTitle, false);
      return;
    }

    if (group.variant === "radio") {
      const radioDefaults = getGroupSelectedFilters(
        group,
        group.defaultSelectedValues
      );
      const next = [
        ...latestSelectedRef.current.filter(
          (selected) => selected.groupTitle !== filter.groupTitle
        ),
        ...radioDefaults,
      ];

      commitSelected(next);
      setGroupOpen(group.title, false);
      return;
    }

    const next = latestSelectedRef.current.filter(
      (f) => !(f.groupTitle === filter.groupTitle && f.value === filter.value)
    );
    commitSelected(next);
    setGroupOpen(filter.groupTitle, false);
  }

  function handleResetAll() {
    if (isAtDefaultState) {
      return;
    }

    if (!areGroupFiltersAtResetState) {
      commitSelected(resetSelectedFilters);
    }

    if (!isPriceAtDefault) {
      selectedPriceFilterGroup?.priceFilter.onReset?.();
    }

    setState((prev) => ({
      ...prev,
      openGroups: {
        ...prev.openGroups,
        ...getClosedGroups(filterGroups),
      },
    }));
    props.onResetAll?.();
  }

  return (
    <div
      className={cn("product-filters", "w-full sm:max-w-64", props.className)}
    >
      <div className="flex items-center justify-between">
        <Button
          className="max-[425px]:w-full"
          onClick={() => setOpen(!state.open)}
          size="sm"
          variant="outline"
        >
          <ListFilter className="mr-1 h-4 w-4" />
          <Text variant="body-sm">
            {state.open
              ? props.microcopies?.hideFiltersButton
              : props.microcopies?.showFiltersButton}{" "}
            ({visibleSelectedFilters.length})
          </Text>
        </Button>
      </div>

      {visibleSelectedFilters.length > 0 && !state.open ? (
        <div className="mt-3 w-full sm:hidden">
          <div className="flex items-center justify-between">
            <Text variant="body-sm-bold">
              {props.microcopies?.selectedFiltersMobileTitle}
            </Text>
            <Button
              className="pr-0"
              disabled={isAtDefaultState}
              onClick={handleResetAll}
              size="sm"
              variant="ghost"
            >
              {props.microcopies?.clearFiltersButton}
            </Button>
          </div>

          <div className="mt-1 flex min-w-0 flex-wrap gap-2">
            {visibleSelectedFilters.map((f) => (
              <Badge
                className="px-2"
                fontWeight="bold"
                icon={<X className="h-full w-full" strokeWidth={3} />}
                key={f.label}
                onClick={() => handleRemove(f)}
                size="default"
                text={f.label}
                variant="outline"
              />
            ))}
          </div>
        </div>
      ) : null}

      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-out",
          props.panelClassName,
          state.open ? "mt-4 grid-rows-[1fr]" : "grid-rows-[0fr]",
          state.open
            ? "max-sm:fixed max-sm:inset-0 max-sm:z-[100] max-sm:mt-0 max-sm:h-screen max-sm:overflow-y-auto max-sm:bg-white"
            : ""
        )}
      >
        <div
          className={cn(
            "min-h-0 overflow-hidden transition-opacity duration-200 ease-out",
            state.open
              ? "p-5 pr-0 pb-0 pl-8 opacity-100 max-sm:flex max-sm:h-screen max-sm:flex-col max-sm:overflow-y-auto max-sm:p-0"
              : "opacity-0"
          )}
        >
          <div className="hidden items-center justify-between border-b px-4 py-3 max-sm:flex">
            <div className="flex items-center gap-2">
              <ListFilter className="h-4 w-4" />
              <Text variant="body-sm-bold">
                {props.microcopies?.filtersTitle ??
                  props.microcopies?.hideFiltersButton}
              </Text>
            </div>
            <Button
              aria-label={props.microcopies?.hideFiltersButton}
              onClick={() => setOpen(false)}
              size="sm"
              variant="ghost"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="mt-2 w-full max-sm:px-4">
            <div className="flex items-center justify-between">
              <Text variant="body-sm-bold">
                {props.microcopies?.selectedFiltersTitle}
              </Text>
              <Button
                disabled={isAtDefaultState}
                onClick={handleResetAll}
                size="sm"
                variant="ghost"
              >
                {props.microcopies?.resetAllButton}
              </Button>
            </div>

            <div className="mt-1 flex min-w-0 flex-wrap gap-2">
              {visibleSelectedFilters.map((f) => (
                <Badge
                  className="px-2"
                  fontWeight="bold"
                  icon={<X className="h-full w-full" strokeWidth={3} />}
                  key={f.label}
                  onClick={() => handleRemove(f)}
                  size="default"
                  text={f.label}
                  variant="outline"
                />
              ))}
            </div>
          </div>

          <div className="w-full max-sm:px-4 max-sm:pb-8">
            {filterGroups.map((group) => {
              if (isPriceFilterGroup(group)) {
                const originalPriceState =
                  initialPriceStateByGroup.current[group.title];

                if (!originalPriceState) {
                  initialPriceStateByGroup.current[group.title] = {
                    range: getDefaultPriceRange(group.priceFilter),
                    selectedPriceType:
                      group.priceFilter.defaultSelectedPriceType ?? "dealer",
                  };
                }

                const baselinePriceState =
                  initialPriceStateByGroup.current[group.title];
                const currentSelectedPriceType =
                  group.priceFilter.selectedPriceType ?? "dealer";
                const isResetDisabled =
                  isPriceRangeEqual(
                    group.priceFilter.value,
                    baselinePriceState.range
                  ) &&
                  currentSelectedPriceType ===
                    baselinePriceState.selectedPriceType;

                const wrappedPriceFilter = {
                  ...group.priceFilter,
                  onFilterAction: (value: [number, number]) => {
                    group.priceFilter.onFilterAction?.(value);

                    const selectedPriceType =
                      group.priceFilter.selectedPriceType ?? "dealer";
                    const defaultPriceType =
                      group.priceFilter.defaultSelectedPriceType ?? "dealer";
                    const isDefaultPriceState =
                      isPriceRangeEqual(value, baselinePriceState.range) &&
                      selectedPriceType === defaultPriceType;

                    const next = syncPriceFilterChip(
                      latestSelectedRef.current,
                      group.title,
                      isDefaultPriceState,
                      value,
                      group.priceFilter.microcopies.currencySymbol
                    );

                    commitSelected(next);
                  },
                  onReset: () => {
                    group.priceFilter.onReset?.();

                    const next = syncPriceFilterChip(
                      latestSelectedRef.current,
                      group.title,
                      true,
                      baselinePriceState.range,
                      group.priceFilter.microcopies.currencySymbol
                    );

                    commitSelected(next);
                  },
                };

                return renderPriceFilterGroup(
                  group,
                  wrappedPriceFilter,
                  isResetDisabled,
                  state.openGroups[group.title] ?? !!group.defaultOpen,
                  (open) => setGroupOpen(group.title, open)
                );
              }

              // Avoid forcing controlled mode by omitting `selectedValues` when
              // rendering AccordionFilter. Instead, subscribe to selection changes
              // and update the local `internalSelected` chips state.
              const uncontrolledGroup = getUncontrolledGroupProps(group);
              const selectedValues = selectedFilters
                .filter((selected) => selected.groupTitle === group.title)
                .map((selected) => selected.value);
              const isGroupOpen =
                state.openGroups[group.title] ??
                group.defaultOpen ??
                selectedValues.length > 0;

              const handleGroupChange = (values: string[]) => {
                const groupSelected = getGroupSelectedFilters(
                  group,
                  values || []
                );
                const current = latestSelectedRef.current;

                const next = [
                  // keep everything except this group's existing entries
                  ...current.filter((f) => f.groupTitle !== group.title),
                  // add the group's current selection
                  ...groupSelected,
                ];

                commitSelected(next);
              };

              const handleGroupReset = () => {
                const current = latestSelectedRef.current;
                const resetValues = getGroupResetValues(group);
                const next = [
                  ...current.filter((f) => f.groupTitle !== group.title),
                  ...resetValues.map((value) => ({
                    groupTitle: group.title,
                    value,
                    label: getFilterLabel(group.options, value),
                  })),
                ];

                commitSelected(next);
              };

              return (
                <AccordionFilter
                  key={group.title}
                  {...uncontrolledGroup}
                  onOpenChange={(open) => setGroupOpen(group.title, open)}
                  onReset={handleGroupReset}
                  onSelectionChange={handleGroupChange}
                  open={isGroupOpen}
                  resetLabel={group.resetLabel}
                  selectedValues={selectedValues}
                />
              );
            })}
          </div>

          <div className="hidden gap-3 border-t bg-white p-4 max-sm:sticky max-sm:bottom-0 max-sm:flex">
            <Button
              className="flex-1"
              disabled={isAtDefaultState}
              onClick={handleResetAll}
              size="sm"
              variant="outline"
            >
              {props.microcopies?.resetFiltersButton ??
                props.microcopies?.resetAllButton}
            </Button>
            <Button className="flex-1" onClick={() => setOpen(false)} size="sm">
              {props.microcopies?.showResultsButton}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
